import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RichMediaState } from '@bonusly/common-action-input';
import { useHttpClient } from '@bonusly/http-client';
import { Recipient, RecipientAPIData } from '@bonusly/recipient-entities';
import { useCurrentUser, useFeatureFlag } from '@bonusly/who-am-i';

import { isEmailIdentifier } from '../../../../utils/email';
import { buildEmailRecipient, normalizeInviteEmail } from '../utils/invite-emails';

type FetchRecipientsProps = {
	include_self?: boolean;
	include_non_receivable?: boolean;
	search_usergroups?: boolean;
	search_users?: boolean;
	// The user to treat as "self" for exclusion. Defaults to the logged-in user,
	// but in proxy/delegated giving this is the principal (the resolved giver) so
	// the principal is excluded and the delegate remains a valid recipient.
	self_user_id?: string;
};

type FetchUseRecipientsProps = {
	query: string;
	state: RichMediaState;
};

type RecipientAPIResponse = {
	data?: {
		result?: RecipientAPIData[] | null;
	};
};

const isInviteEmailQuery = (value: string) => isEmailIdentifier(normalizeInviteEmail(value));

const getRecipientResult = (response: RecipientAPIResponse | null | undefined): RecipientAPIData[] => {
	const result = response?.data?.result;

	return Array.isArray(result) ? result : [];
};

// @TODO reuse this logic for other common (feedback be sure not include groups)
export const useFetchRecipients = (params: FetchRecipientsProps = {}) => {
	const currentUser = useCurrentUser();
	const inviteByEmailEnabled = useFeatureFlag('plg-invite-by-email');
	const queryClient = useQueryClient();
	const http = useHttpClient('v2');

	return useCallback(
		async ({ query, state }: FetchUseRecipientsProps): Promise<Recipient[]> => {
			const search = query.trim();
			const taggedRecipients = [...state.mentions, ...state.recipients];
			const { include_self, self_user_id, ...rest } = params;
			// In proxy giving self_user_id is the principal (the resolved giver);
			// otherwise fall back to the logged-in user.
			const selfId = self_user_id ?? currentUser.id;
			const inviteEmail = normalizeInviteEmail(search);
			const shouldSuggestEmailInvite = inviteByEmailEnabled && isInviteEmailQuery(search);

			const buildRecipientsList = (data: RecipientAPIData[]) => {
				const recipients: Recipient[] = [];
				const seen = new Set<string>();
				data.forEach((r) => {
					const recipient = new Recipient(r);
					const dedupeKey = `${recipient.type}:${recipient.id}`;
					if (seen.has(dedupeKey)) return;
					if (!include_self && recipient.type === 'username' && recipient.id === selfId) {
						return;
					}
					if (!include_self && recipient.type === 'group') {
						// exclude self user from groups
						const recipients = recipient.userIds.filter((id) => id !== selfId);
						recipient.userIds = recipients;
						// exclude group if empty
						if (recipients.length === 0) return;
					}
					if (taggedRecipients.some((tagged) => tagged.id === recipient.id)) {
						return;
					}
					seen.add(dedupeKey);
					recipients.push(recipient);
				});
				return recipients;
			};

			if (!search) {
				const data = await queryClient.fetchQuery(['recipients'], () => http.get('/recipients'), { staleTime: Infinity });
				return buildRecipientsList(getRecipientResult(data));
			}

			const all = { search, ...rest };
			const data = await queryClient.fetchQuery(['rich-users', all], () => {
				return http.get('/users/autocomplete', { params: all });
			});

			const recipients = buildRecipientsList(getRecipientResult(data));
			const hasExactEmailMatch = getRecipientResult(data).some((recipient: any) => {
				const candidate = (recipient.email || recipient.username || '').toString().toLowerCase();
				return candidate === inviteEmail;
			});
			const isTaggedAlready = taggedRecipients.some((tagged) => {
				return (tagged.id || tagged.username || '').toString().toLowerCase() === inviteEmail;
			});
			const currentUserEmail = ((currentUser as any).email || '').toString().toLowerCase();
			const isCurrentUserEmail = currentUserEmail === inviteEmail;

			if (shouldSuggestEmailInvite && !hasExactEmailMatch && !isTaggedAlready && (include_self || !isCurrentUserEmail)) {
				recipients.unshift(buildEmailRecipient(inviteEmail));
			}

			return recipients;
		},
		[currentUser, http, inviteByEmailEnabled, params, queryClient]
	);
};
